/**
 * `wayai relationship-types …` — the schema a relationship's metadata conforms
 * to. Top-level: WayAI owns no `relationship-types` concept
 * (docs/reference/rekor-platform-contracts.md §3.2).
 */

import { Command } from 'commander';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { historyQuery, outputFormat, parseData, pathSegment, requireBase } from '../helpers.js';
import { confirm } from '../../lib/utils.js';

export function buildRelationshipTypesCommand(): Command {
  const relationshipTypes = new Command('relationship-types').description(
    "Manage relationship types (the schema for a relationship's metadata)",
  );

  relationshipTypes
    .command('list')
    .description('List all relationship types in a base')
    .action(async function (this: Command) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('GET', `/v1/${base}/relationship-types`),
        outputFormat(this),
      );
    });

  relationshipTypes
    .command('get <rel_type>')
    .description('Get a relationship type')
    .action(async function (this: Command, relType: string) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request('GET', `/v1/${base}/relationship-types/${pathSegment(relType, 'rel_type')}`),
        outputFormat(this),
      );
    });

  relationshipTypes
    .command('upsert <rel_type>')
    .description('Create or update a relationship type')
    .option('--description <desc>', 'Description')
    .option(
      '--schema <json>',
      'JSON Schema for the relationship data (inline JSON or @filename). Omit to allow any data.',
    )
    .option(
      '--source-record-types <json>',
      'JSON array of allowed source record_types (inline JSON or @filename)',
    )
    .option(
      '--target-record-types <json>',
      'JSON array of allowed target record_types (inline JSON or @filename)',
    )
    .action(async function (
      this: Command,
      relType: string,
      opts: {
        description?: string;
        schema?: string;
        sourceRecordTypes?: string;
        targetRecordTypes?: string;
      },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const body: Record<string, unknown> = {};
      if (opts.description) body.description = opts.description;
      if (opts.schema) body.data_schema = parseData(opts.schema);
      if (opts.sourceRecordTypes) body.source_record_types = parseData(opts.sourceRecordTypes);
      if (opts.targetRecordTypes) body.target_record_types = parseData(opts.targetRecordTypes);
      printOutput(
        await client.request('PUT', `/v1/${base}/relationship-types/${pathSegment(relType, 'rel_type')}`, body),
        outputFormat(this),
      );
    });

  relationshipTypes
    .command('delete <rel_type>')
    .description('Delete a relationship type')
    .option('-y, --yes', 'Skip confirmation prompt')
    .action(async function (this: Command, relType: string, opts: { yes?: boolean }) {
      if (
        !opts.yes &&
        !(await confirm(`Delete relationship type "${relType}"? This cannot be undone.`))
      ) {
        console.log('Aborted');
        return;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      await client.request('DELETE', `/v1/${base}/relationship-types/${pathSegment(relType, 'rel_type')}`);
      console.log('Deleted');
    });

  relationshipTypes
    .command('history <id>')
    .description(
      'View the change history (audit log) for a relationship type — every config version, who changed it, and when',
    )
    .option('--limit <n>', 'Max results')
    .option('--offset <n>', 'Pagination offset')
    .option('--diff', 'Include field-level diffs between versions')
    .action(async function (
      this: Command,
      id: string,
      opts: { limit?: string; offset?: string; diff?: boolean },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(
        await client.request(
          'GET',
          `/v1/${base}/audit/relationship-types/${pathSegment(id, 'rel_type')}${historyQuery(opts)}`,
        ),
        outputFormat(this),
      );
    });

  return relationshipTypes;
}
